import { useAuthStore } from '@stores/auth.store';
import { useCallback, useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';


/**
 * Manages the Socket.io connection lifecycle.
 *
 * Connects on mount using the JWT from auth store. The socket is held in
 * STATE (not a ref) and `on`/`off`/`emit` depend on it, so that when the
 * access token refreshes — which tears down the old socket and creates a
 * new one — `on`/`off` change identity and every consumer effect that lists
 * them in its dependency array re-binds its listeners to the NEW socket.
 *
 * (Previously the handlers were bound once to the original socket and went
 * silently dead after the first token refresh, so realtime updates — e.g. a
 * new inbox conversation — stopped appearing until a manual page reload.)
 */
export function useSocket(): {
  socket: Socket | null;
  isConnected: boolean;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler: (...args: unknown[]) => void) => void;
  emit: (event: string, data?: unknown) => void;
} {
  const token = useAuthStore((s) => s.token);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!token) {
      setSocket(null);
      setIsConnected(false);
      return;
    }

    const url = import.meta.env.VITE_API_URL
      ? new URL(import.meta.env.VITE_API_URL).origin
      : window.location.origin;

    const sock = io(url, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    sock.on('connect', () => setIsConnected(true));
    sock.on('disconnect', () => setIsConnected(false));

    setSocket(sock);

    return () => {
      sock.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [token]);

  const on = useCallback(
    (event: string, handler: (...args: unknown[]) => void) => {
      socket?.on(event, handler);
    },
    [socket],
  );

  const off = useCallback(
    (event: string, handler: (...args: unknown[]) => void) => {
      socket?.off(event, handler);
    },
    [socket],
  );

  const emit = useCallback(
    (event: string, data?: unknown) => {
      socket?.emit(event, data);
    },
    [socket],
  );

  return { socket, isConnected, on, off, emit };
}
